import {
  PartnerDetail,
  ConsolidatorByCity,
  ConsolidatorByCityData,
  PartnerBank,
  ClientNamePOS,
  ClientNamePOSData,
  VaNumbers,
  AreaDeliveries,
  DistrictData,
  PosOwnedList,
  PartnerLocation,
  Country,
  City,
  District
} from "@/domain/entities/Partner";
import { AxiosResponse } from "axios";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";
import { Pagination } from "@/domain/entities/Pagination";
import { ClientPartnerOptions } from "@/domain/entities/CnManifest";
import formatInputMoney from "@/app/infrastructures/misc/common-library/FormatInputMoney";
import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";
export class PartnerMapper {
  private mapBasicInfo(data: any) {
    return {
      partnerId: data.id,
      partnerType: data.type,
      partnerName: data.name,
      partnerCode: data.code,
      partnerElexysCode: data.partner_external_code,
      partnerBalance: data.balance,
      partnerAddress: data.address,
      partnerLatitude: data.lat,
      partnerLongitude: data.long,
      partnerReferralCode: data.partner_referral_code ?? "-",
      partnerReferrerCode: data.partner_referrer_code ?? "-",
      partnerOwnershipName: data.partner_ownership_name ?? "",
      lastTransaction: data.last_transaction ?? ""
    } as PartnerDetail;
  }

  private mapLocationInfo(data: any) {
    const location = data.partner_location ?? {};

    const partnerCity = (data.partner_location_list ?? [])
      .filter((item: any) => !item.default_location)
      .map((item: any) => ({
        code: item.city?.code,
        name: item.city?.name
      }));

    return {
      partnerLocationId: location.id ?? 0,
      partnerLocationCityCode: location.city_code ?? "",
      partnerDistrict: location.district?.name ?? "",
      partnerDistrictCode: location.district_code ?? "",
      partnerCity
    } as PartnerDetail;
  }

  private mapParentInfo(data: any) {
    return {
      partnerParentType: data.partner_parent_partner_type,
      partnerParentId: data.parent_id,
      partnerParentName: data.parent_partner_name
    } as PartnerDetail;
  }

  private mapContactInfo(data: any) {
    return {
      partnerPhoneNumber: data.partner_phone_number,
      partnerTaxNumber: data.partner_tax_number,
      partnerStartContract: data.partner_contract_start,
      partnerEndContract: data.partner_contract_end,
      partnerContactName: data.partner_contact_name,
      partnerContactJobTitle: data.partner_contact_job_title,
      partnerContactEmail: data.partner_contact_email,
      partnerContactPhoneNumber: data.partner_contact_phone_number
    } as PartnerDetail;
  }

  private mapStatusInfo(data: any) {
    return {
      partnerContractStatus: data.partner_status,
      partnerIsBanned: data.partner_is_banned,
      partnerBannedReason: data.partner_banned_reason ?? "-",
      partnerCreatedAt: data.created_at,
      partnerUpdatedAt: data.updated_at,
      partnerCreatedBy: data.created_name,
      partnerUpdatedBy: data.updated_name
    } as PartnerDetail;
  }

  private mapVaNumbers(data: any) {
    const vaNumbers = (data.va_numbers ?? []).map(
      (item: any) =>
        new VaNumbers(
          item.bank_account_account_number,
          item.bank_account_bank_logo ?? "",
          item.bank_account_bank_name,
          item.bank_account_label
        )
    );

    return { vaNumbers };
  }

  private mapAreaDeliveries(data: any) {
    const areaDeliveries = (data.partner_area_deliveries ?? []).map(
      (item: any) =>
        new AreaDeliveries(
          item.city_code,
          item.city_name,
          (item.districts ?? []).map((d: any) => ({
            code: d.code,
            name: d.name
          })) as DistrictData[]
        )
    );

    return { partnerAreaDeliveries: areaDeliveries };
  }

  private mapPosOwnedList(data: any) {
    const posOwnedList = (data.partner_pos_owned_list ?? []).map(
      (item: any) =>
        new PosOwnedList(
          item.id,
          item.name,
          item.district_code,
          item.district_name,
          item.city_code,
          item.city_name
        )
    );

    return { posOwnedList };
  }

 

  private mapPosInfo(data: any) {
    const saldoDeposit = data.partner_pos_saldo_deposit ?? 0;

    return {
      partnerIsCodDelivery: data.partner_is_cod_delivery,
      partnerIsCodBooking: data.partner_is_cod_booking,
      partnerIsPcu: data.partner_is_pcu,
      partnerIsAllowBookVippack: data.partner_is_allow_book_vippack,
      partnerIdSttReturn: data.partner_id_stt_return,
      partnerNameSttReturn: data.partner_name_stt_return,
      partnerTypeSttReturn: data.partner_type_stt_return,
      partnerPosType: data.partner_pos_type,
      partnerPosParentId: data.partner_pos_parent_id,
      partnerPosParentName: data.partner_pos_parent_name,
      partnerPosParentIdDefault: data.partner_pos_parent_id,
      partnerPosParentNameDefault: data.partner_pos_parent_name,
      partnerPosReverseJourney: `${data.pos_code_default_reverse_journey ?? ""} - ${data.pos_name_default_reverse_journey ?? ""}`,
      partnerPosBranchCommission: data.partner_pos_branch_commission ?? 0,
      partnerPosReverseJourneyCode: data.pos_code_default_reverse_journey,
      partnerPosReverseJourneyName: data.pos_name_default_reverse_journey,
      partnerPosAttachFiles: Array.isArray(data.partner_pos_attach_files) ? data.partner_pos_attach_files : [],
      partnerPosSaldoDeposit: saldoDeposit,
      partnerPosSaldoDepositFormated: formatInputMoney(saldoDeposit),
      partnerPosFirstDateTopup: data.partner_pos_first_date_topup,
      partnerPosFirstTransaction: data.partner_pos_first_transaction,
      partnerPosPickupManifest: data.partner_pos_pickup_manifest,
      partnerPosTieringLevel: data.partner_pos_tiering_level?.toLowerCase(),
      partnerOwnershipId: data.partner_ownership_id || 0,
      partnerOwnershipName: data.partner_ownership_name || "",
      partnerOwnershipIdDefault: data.partner_ownership_id || 0,
      partnerOwnershipNameDefault: data.partner_ownership_name || "",
      partnerIsPosOwnSales: data.partner_is_pos_ownsales,
    } as PartnerDetail;
  }

  private mapPartnerLocation(data: any) {
    const location = data.partner_location;
    return {
      partnerLocation: location
        ? new PartnerLocation({
            id: location.id,
            partnerId: location.partner_id,
            districtCode: location.district_code,
            cityCode: location.city_code,
            createdAt: location.created_at,
            updatedAt: location.updated_at,
            country: location.country
              ? new Country({
                  id: location.country.id,
                  code: location.country.code,
                  name: location.country.name,
                  status: location.country.status,
                  currency: location.country.currency,
                  createdBy: location.country.created_by,
                  createdAt: location.country.created_at,
                  updatedBy: location.country.updated_by,
                  updatedAt: location.country.updated_at
                })
              : new Country(),
            city: location.city
              ? new City({
                  id: location.city.id,
                  code: location.city.code,
                  name: location.city.name,
                  isActive: location.city.is_active,
                  isSundayDelivery: location.city.is_sunday_delivery,
                  timezone: location.city.timezone,
                  freeTradeZone: location.city.free_trade_zone,
                  countryId: location.city.country_id,
                  originTransit: location.city.origin_transit,
                  destinationTransit: location.city.destination_transit,
                  districtDefault: location.city.district_default,
                  createdBy: location.city.created_by,
                  createdAt: location.city.created_at,
                  updatedBy: location.city.updated_by,
                  updatedAt: location.city.updated_at
                })
              : new City(),
            district: location.district
              ? new District({
                  id: location.district.id,
                  code: location.district.code,
                  name: location.district.name,
                  type: location.district.type,
                  cityId: location.district.city_id,
                  ursaCode: location.district.ursa_code,
                  originZipCode: location.district.origin_zip_code,
                  destinationZipCode: location.district.destination_zip_code,
                  vendorCode: location.district.vendor_code,
                  status: location.district.status,
                  isCod: location.district.is_cod,
                  isPtpos: location.district.is_ptpos,
                  createdBy: location.district.created_by,
                  updatedBy: location.district.updated_by,
                  slaOrigin: location.district.sla_origin,
                  slaDestination: location.district.sla_destination,
                  minSlaOrigin: location.district.min_sla_origin,
                  maxSlaOrigin: location.district.max_sla_origin,
                  minSlaDestination: location.district.min_sla_destination,
                  maxSlaDestination: location.district.max_sla_destination,
                  city: location.district.city,
                  vendorOriginCode: location.district.vendor_origin_code,
                  vendorDestinationCode:
                    location.district.vendor_destination_code,
                  vendorBranchCode: location.district.vendor_branch_code,
                  createdAt: location.district.created_at,
                  updatedAt: location.district.updated_at,
                  areaType: location.district.area_type
                })
              : new District(),
            defaultLocation: location.default_location,
            partner: location.partner
          })
        : new PartnerLocation()
    };
  }

  public convertPartnerDetailFromApi(response: AxiosResponse): PartnerDetail {
    const data = response.data?.data ?? {};

    return new PartnerDetail({
      ...this.mapBasicInfo(data),
      ...this.mapLocationInfo(data),
      ...this.mapParentInfo(data),
      ...this.mapContactInfo(data),
      ...this.mapStatusInfo(data),
      ...this.mapPosInfo(data),
      ...this.mapVaNumbers(data),
      ...this.mapAreaDeliveries(data),
      ...this.mapPosOwnedList(data),
      ...this.mapPartnerLocation(data)
    });
  }

  public convertPayloadData(res: AxiosResponse<any>): ResponsePayload {
    const { data } = res;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertConsolidatorByCityFromApi(
    result: AxiosResponse<any>
  ): ConsolidatorByCity {
    const { data } = result;

    const districtListData: ConsolidatorByCityData[] = [];
    if (Array.isArray(data.data)) {
      data.data.forEach((item: any) => {
        districtListData.push(
          new ConsolidatorByCityData(
            item.id,
            item.name,
            item.address,
            item.balance,
            item.code,
            item.lat,
            item.long,
            item.type,
            item.createdAt,
            item.updatedAt,
            item.parentId,
            item.parentName,
            item.parentType,
            item.contractEnd,
            item.contractStatus,
            item.isBanned,
            item.isRegistry
          )
        );
      });
    }

    const pagination = new Pagination(data.meta.page, data.meta.limit);

    return new ConsolidatorByCity(pagination, districtListData);
  }

  public convertListPartnerBankFromApi(res: AxiosResponse<any>): PartnerBank[] {
    const { data } = res;
    return data.data.map((item: any) => {
      return new PartnerBank({
        bankCode: item.code,
        bankName: item.name,
        bankRouting: item.routing_code
      });
    });
  }

  public convertChangeDataFromApi(result: AxiosResponse<any>): ResponsePayload {
    const { data } = result;
    return new ResponsePayload({
      success: data.success,
      message: data.message
    });
  }

  public convertClientNamePOSFromApi(
    result: AxiosResponse<any>
  ): ClientNamePOS {
    const { data } = result;

    const clientListData: ClientNamePOSData[] = [];
    if (Array.isArray(data.data)) {
      data.data.forEach((item: any) => {
        clientListData.push(
          new ClientNamePOSData({
            clientId: item.client_id,
            clientCode: item.client_code,
            clientName: item.client_name,
            clientStatus: item.client_is_banned,
            clientIsCOD: item.client_is_cod,
            clientIsDO: item.client_is_do,
            clientOriginCity: item.client_origin_city,
            clientCategory: item.client_category,
            clientPackageType: item.client_package_type,
            clientCodConfigAmount: item.client_cod_config_amount,
            clientAlias: item.client_alias,
            clientCountryCode:
              item.client_country_code || item.country_code || "",
            clientPartner: convertSnakeToCamel(item.client_partner),
            clientPhone: item.client_phone || "",
            clientAddress: item.client_address || "",
            clientDistrictCode: item.client_district_code || "",
            clientDistrictId:
              typeof item.client_district_id === "number"
                ? item.client_district_id
                : undefined,
            clientDistrictName: item.client_district_name || "",
            clientDistrictOriginZipCode: Array.isArray(
              item.client_district_origin_zip_code
            )
              ? item.client_district_origin_zip_code
              : [],
            clientIsDfod:
              item.client_is_dfod === true
                ? true
                : item.client_is_dfod === false
                  ? false
                  : undefined,
            isShowShippingRate:
              typeof item.client_show_shipping_tariff === "boolean"
                ? item.client_show_shipping_tariff
                : undefined
          })
        );
      });
    }

    const pagination = new Pagination(
      data.meta.page,
      data.meta.limit,
      data.meta.total_data
    );

    return new ClientNamePOS(pagination, clientListData);
  }

  public convertPartnerOptionsFromApi(
    result: AxiosResponse
  ): ClientPartnerOptions[] {
    try {
      const {
        data: { data }
      } = result;
      return data.map(
        (item: any) =>
          new ClientPartnerOptions({
            id: item.id,
            name: item.name,
            code: item.code,
            type: item.type,
            contractStatus: item.contract_status,
            partnerExternalCode: item.partner_external_code,
            cityCode : item.city_code,
            cityName: item.partner_location?.district.name || "",
            districtCode: item.partner_location?.district_code || "",
            partnerIsCodDelivery: item.partner_is_cod_delivery || "",
            ownershipId: item.ownership_id
          })
      );
    } catch (e) {
      return [];
    }
  }
}
